import { useState, createContext, useCallback, useMemo, useEffect, useRef } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './routes/ProtectedRoute';
import RedirectRoute from './routes/RedirectRoute';
import NavBar from './components/NavBar/NavBar';
import SidePanel from './components/SidePanel/SidePanel';
import Login from './pages/Login/Login';
import ResetPassword from './pages/ResetPassword';
import Unauthorized from './pages/Unauthorized';
import Shipment from './pages/Shipment/Shipment';
import Shipments from './pages/Shipments/Shipments';
import Users from './pages/Users/Users';
import Facilities from './pages/Facilities/Facilities';
import Facility from './pages/Facility/Facility';
import {
  logout,
  getAuthToken,
  setAuthHeader,
  refreshAccessToken,
  shouldRefreshToken,
} from './services/authService';
import DriverSlotAssignment from './pages/DriverManagement/DriverSlotAssignment';
import DockManagement from './pages/DockManagement/DockManagement';
import { twMerge } from 'tailwind-merge';
import User from './pages/User/User';
import { YardSpotManagement } from './pages/YardManagement';
import DriverComms from './pages/DriverComms';
import WhiteBoard from './pages/WhiteBoard';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/Tasks/TaskDetail';
import TaskApp from './pages/TaskApp';
import Trailers from './pages/Trailers';
import AndroidTopBar from './components/AndroidTopBar/AndroidTopBar';
import Dashboard from './pages/Dashboard';
// import Reports from './pages/Reports'; // Moved to Trailers page
import Reports from './pages/Reports';
import CarrierTrailers from './pages/CarrierTrailers';
import TransportTrailers from './pages/TransportTrailers';
import { detectAndroidDevice } from './utils/deviceDetection';
import { isSuperAdmin, isAdmin, isDockCoordinator, isCarrier, isTransport, canAccessTaskApp } from './utils/roleHelper';
import YardSpotterManagement from "./pages/YardSpotterApp/YardSpotterManagement";
import TrailerHistoryPage from './pages/YardManagement/TrailerHistoryPage';
import { fetchCurrentUser } from './hooks/useCurrentUser';
import { useForceLogout } from './hooks/useForceLogout';
export const UserContext = createContext({});


// Add GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

interface UserType {
  id: string;
  name: string;
  email: string;
  roles: string[];
  canAccessUserManagement: boolean;
  pages: string[];
}
// interface UserContextType {
//   user: User | null;
//   setUser: () => void;
//   unsetUser: () => void;
// }

const App = () => {
  const [showSidePanel, setShowSidePanel] = useState(true);
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [ymsEnabled, setYmsEnabled] = useState(false);
  const [ymsLoading, setYmsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isRedirectingRef = useRef(false);

  // Resilient token refresh: runs every 30s, survives route changes, self-heals on failure
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (!shouldRefreshToken()) return;
      console.log("in refresh timer success")

      try {
        await refreshAccessToken();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Token will be retried on next interval tick.
        // If refresh token itself is expired, refreshAccessToken
        // will throw and the cookie will eventually expire,
        // causing the existing auth check to redirect to login.
      }
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, []); // No dependencies — survives route changes

  // Check if we're in android mode based on device detection or user role
  const isAndroidDevice = detectAndroidDevice();
  const isSpotterRole = user?.roles.some(role => role.toLowerCase().includes('spotter')) || false;
  const isAndroidMode = isAndroidDevice || isSpotterRole;

  useEffect(() => {
    // Handle viewport meta tag for android mode
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (isAndroidMode) {
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=no, viewport-fit=cover');
      }
      // Add android-specific styles to body
      document.body.classList.add('android-mode');
    } else {
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
      // Remove android-specific styles from body
      document.body.classList.remove('android-mode');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('android-mode');
    };
  }, [isAndroidMode]);

  // Fetch YMS status when user is set (e.g., after login)
  useEffect(() => {
    const fetchYmsStatus = async () => {
      if (!user) {
        setYmsLoading(false);
        return;
      }

      if (isCarrier(user) || isTransport(user)) {
        setYmsEnabled(false);
        setYmsLoading(false);
        return;
      }

      if (ymsEnabled) {
        setYmsLoading(false);
        return; // Already set
      }

      setYmsLoading(true);

      const authToken = getAuthToken();

      if (!authToken) {
        setYmsLoading(false);
        return;
      }

      try {
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: `
              query {
                facilities {
                  id
                  activation {
                    yms
                  }
                }
              }
            `
          })
        });

        const result = await response.json();
        if (result.data?.facilities) {
          const hasYms = result.data.facilities.some((facility: any) => 
            facility.activation?.yms === true
          );
          setYmsEnabled(hasYms);
        } else {
          setYmsEnabled(false);
        }
      } catch (error) {
        console.error('Failed to fetch YMS status:', error);
        setYmsEnabled(false);
      } finally {
        setYmsLoading(false);
      }
    };

    fetchYmsStatus();
  }, [user, ymsEnabled]);

  useEffect(() => {
    const checkAuthentication = async () => {
      // Skip auth check if we're on login or reset password pages
      if (location.pathname === '/login' || location.pathname === '/resetPassword') {
        isRedirectingRef.current = false; // Reset redirect flag when on login pages
        setLoading(false);
        return;
      }

      // Prevent multiple simultaneous redirects
      if (isRedirectingRef.current) {
        return;
      }

      const authToken = getAuthToken();
      const userFromStorage = localStorage.getItem('user');

      // Handle SSO return: token exists but no user in localStorage
      // This happens when user returns from SSO - backend set the cookie
      if (authToken && !userFromStorage) {
        // Set auth header for API calls
        setAuthHeader(authToken);

        // Check if token needs refresh before proceeding
        if (shouldRefreshToken()) {
          try {
            await refreshAccessToken();
          } catch (error) {
            console.error('Token refresh failed on SSO return:', error);
            logout();
            navigate('/login', { replace: true });
            setLoading(false);
            return;
          }
        }

        // For SSO users, fetch full user context from currentUser GraphQL query
        // This provides roles, permissions, facilities, and other data not in JWT
        try {
          const currentUser = await fetchCurrentUser();
          if (currentUser) {
            // Map CurrentUser to UserType for app context
            const ssoUser: UserType = {
              id: currentUser.id,
              email: currentUser.email,
              name: currentUser.username || '',
              roles: currentUser.roles,
              canAccessUserManagement: currentUser.roles.some(
                role => role.toLowerCase() === 'admin' || role.toLowerCase() === 'super_admin'
              ),
              pages: currentUser.pages
            };

            // Store user in localStorage for future page loads
            localStorage.setItem('user', JSON.stringify(ssoUser));
            setUser(ssoUser);

            // Check if any facility has YMS enabled from currentUser response
            if (currentUser.facilities && currentUser.facilities.length > 0) {
              // Facilities are available from currentUser, YMS check will happen in useEffect
            }

            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch current user for SSO:', error);
        }

        // If we couldn't fetch user data, redirect to login
        logout();
        navigate('/login', { replace: true });
        setLoading(false);
        return;
      }

      // No user in storage at all — nothing to recover
      if (!userFromStorage) {
        isRedirectingRef.current = true;
        setUser(null);
        logout();
        navigate('/login', { replace: true });
        setLoading(false);
        return;
      }

      // User exists in storage but token is missing — try refresh before giving up
      if (!authToken) {
        try {
          const newToken = await refreshAccessToken();
          setAuthHeader(newToken);
        } catch {
          isRedirectingRef.current = true;
          setUser(null);
          logout();
          navigate('/login', { replace: true });
          setLoading(false);
          return;
        }
      }

      // Set auth header for API calls (use fresh token after possible refresh)
      const currentToken = getAuthToken();
      setAuthHeader(currentToken);

      try {
        // Parse user to check their role
        const parsedUser = JSON.parse(userFromStorage);


        // Use different validation query based on user role
        let validationQuery;
        const isCarrierUser = parsedUser.roles && parsedUser.roles.some((role: string) => role.toLowerCase() === 'carrier');
        const isTransportUser = parsedUser.roles && parsedUser.roles.some((role: string) => role.toLowerCase() === 'transport');

        if (isCarrierUser) {
          // Carrier users - validate with myCarrierTrailers query
          validationQuery = `
            query {
              myCarrierTrailers {
                totalCount
              }
            }
          `;
        } else if (isTransportUser) {
          // Transport users - validate with transportTrailers query
          validationQuery = `
            query {
              transportTrailers {
                totalCount
              }
            }
          `;
        } else {
          // Other users - validate with facilities query
          validationQuery = `
            query {
              facilities {
                id
                name
                activation {
                  digital_guard
                  digital_bol
                  yms
                  rtls
                  automated_gate
                  empty_drop_enabled
                }
              }
            }
          `;
        }

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`
          },
          body: JSON.stringify({
            query: validationQuery
          })
        });

        if (response.status === 401) {
          // Token is invalid, redirect to login
          isRedirectingRef.current = true;
          setUser(null); // Clear user state immediately
          logout();
          navigate('/login', { replace: true });
          setLoading(false);
          return;
        }

        const result = await response.json();
        
        if (result.errors) {
          // GraphQL errors might indicate auth issues
          console.error('GraphQL errors:', result.errors);
          isRedirectingRef.current = true;
          setUser(null); // Clear user state immediately
          logout();
          navigate('/login', { replace: true });
          setLoading(false);
          return;
        }

        // Token is valid, set user
        const validatedUser = parsedUser;
        validatedUser.canAccessUserManagement = true;
        setUser(validatedUser);

        // Check if any facility has YMS enabled (only for non-carrier users)
        if (!isCarrierUser && !isTransportUser && result.data?.facilities) {
          const hasYms = result.data.facilities.some((facility: any) =>
            facility.activation?.yms === true
          );
          setYmsEnabled(hasYms);
        } else if (isCarrierUser || isTransportUser) {
          // Carrier and transport users don't need YMS
          setYmsEnabled(false);
        } else {
          // No facilities data, default to false
          setYmsEnabled(false);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        isRedirectingRef.current = true;
        setUser(null); // Clear user state immediately
        logout();
        navigate('/login', { replace: true });
      }

      setLoading(false);
    };

    checkAuthentication();
  }, [navigate, location]);

  const handleSetUser = useCallback((user: UserType) => {
    const { id, name, roles, email, canAccessUserManagement, pages } = user;
    setUser({ id, name, roles, email, canAccessUserManagement, pages });
  }, []);
  const handleUnsetUser = useCallback(() => setUser(null), []);

  // Connect to auth WebSocket for SSO force logout (IdP-initiated SLO)
  useForceLogout({
    isAuthenticated: !!user,
    onLogout: handleUnsetUser,
  });

  const userContextValue = useMemo(
    () => ({ user, setUser: handleSetUser, unsetUser: handleUnsetUser, ymsEnabled }),
    [user, handleSetUser, handleUnsetUser, ymsEnabled]
  );

  const toggleSidePanel = () => {
    setShowSidePanel(!showSidePanel);
  };

  // Check if user has access to the current page
  const hasPageAccess = useCallback((pathname: string, userPages: string[] | undefined) => {
    if (!userPages || userPages.length === 0) return false;

    // If user has "*" in pages, allow all routes
    if (userPages.includes('*')) return true;

    // Get the base path (first segment after /)
    const basePath = '/' + pathname.split('/')[1];

    // Always allow access to /account route
    if (basePath === '/account') return true;

    // Map route paths to page names (only add exceptions where route name differs from page name)
    const routeToPageMap: Record<string, string> = {
      '/carrier': 'carriertrailers',
      '/transport': 'trailers',
    };

    // Use custom mapping if exists, otherwise strip leading slash to get page name
    const pageName = routeToPageMap[basePath] || basePath.substring(1);

    if (!pageName) return true; // Allow unknown routes to be handled by existing logic

    return userPages.includes(pageName);
  }, []);

  // Check page access on route change
  useEffect(() => {
    // Skip check for login, reset password, and unauthorized pages
    if (location.pathname === '/login' || location.pathname === '/resetPassword' || location.pathname === '/unauthorized' || location.pathname === '/') {
      return;
    }

    // Check if user has access to the current page
    if (user && user.pages) {
      const hasAccess = hasPageAccess(location.pathname, user.pages);

      if (!hasAccess) {
        navigate('/unauthorized', { replace: true, state: { attemptedPath: location.pathname } });
      }
    }
  }, [location.pathname, user, hasPageAccess, navigate]);

  // Calculate allowUser based on current user state, not localStorage
  let allowUser = true;
  if (user) {
    const allowedUsers = ['chad.marks@bluetriton.com', 'anthony.cox@bluetriton.com'];
    const userEmail = user.email;
    if (
      allowedUsers.includes(userEmail) ||
      userEmail.split('@')[1] === 'freightroll.com'
    ) {
      allowUser = true;
    }
  } else {
    allowUser = false; // No user means not allowed
  }

  const canViewFacilities = true;

  // Helper function to get the default route based on mode and role
  const getDefaultRoute = () => {
    if (isAndroidMode) {
      return '/taskapp';
    }
    if (isCarrier(user)) {
      return '/carrier/trailers';
    }
    if (isTransport(user)) {
      return '/transport/trailers';
    }
    
    // Check if user has specific page access
    if (user?.pages) {
      if (user.pages.includes('*')) {
        // User has access to everything, use normal logic
        if (!ymsEnabled) {
          return '/shipments';
        }
        return '/dashboard';
      }
      
      // Check what pages the user actually has access to
      if (ymsEnabled && user.pages.includes('dashboard')) {
        return '/dashboard';
      }
      if (user.pages.includes('shipments')) {
        return '/shipments';
      }
      
      // If user has no recognized pages, redirect to unauthorized
      return '/unauthorized';
    }
    
    // Default fallback
    if (!ymsEnabled) {
      return '/shipments';
    }
    return '/dashboard';
  };

  // Component for root route that waits for YMS status
  const DefaultRouteRedirect = () => {
    if (ymsLoading) {
      return <div>Loading...</div>;
    }
    return <Navigate to={getDefaultRoute()} replace />;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

    return (
        <UserContext.Provider value={userContextValue}>
            <div className={twMerge(
                'bg-[#EEEEEE] min-h-screen flex',
                isAndroidMode ? 'w-screen max-w-none m-0 p-0' : ''
            )}>
                {user && !isAndroidMode && (
                    <SidePanel
                        showing={showSidePanel}
                        showUserManagement={isAdmin(user)}
                        user={user}
                        ymsEnabled={ymsEnabled}
                    />
                )}
                <div
                    className={twMerge(
                        'w-full',
                        !isAndroidMode && showSidePanel ? 'ml-48 w-[calc(100vw-192px)]' : '',
                        isAndroidMode ? 'min-h-screen w-screen max-w-none overflow-x-hidden m-0 p-0' : ''
                    )}
                >
                    {user && !isAndroidMode && <NavBar toggleSidePanel={toggleSidePanel}/>}
                    {isAndroidMode && user && <AndroidTopBar/>}
                    <Routes>
                        <Route path="login" element={<Login/>}/>
                        <Route path="resetPassword" element={<ResetPassword/>}/>
                        <Route path="unauthorized" element={<Unauthorized/>}/>
                        <Route element={<ProtectedRoute isAllowed={!!user}/>}>
                            <Route path="/" element={<DefaultRouteRedirect/>}/>
                            <Route path="shipments" element={<Shipments/>}/>
                            <Route path="shipments/:shipmentId" element={<Shipment/>}/>
                            <Route path="account" element={<User/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={canAccessTaskApp(user)}/>}>
                            <Route path="taskapp" element={<TaskApp/>}/>
                            <Route path="tasks" element={<Tasks/>}/>
                            <Route path="tasks/:taskId" element={<TaskDetail/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={isCarrier(user)}/>}>
                            <Route path="carrier/trailers" element={<CarrierTrailers/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={isTransport(user)}/>}>
                            <Route path="transport/trailers" element={<TransportTrailers/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={isDockCoordinator(user)}/>}>
                            <Route path="dashboard" element={<Dashboard/>}/>
                            <Route path="drivers/unassigned_slot" element={<DriverSlotAssignment/>}/>
                            <Route path="drivers/comms/*" element={<DriverComms/>}/>
                            <Route path="whiteboard" element={<WhiteBoard/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={canAccessTaskApp(user)}/>}>
                            <Route path="dock" element={<DockManagement/>}/>
                            <Route path="yard/spots" element={<YardSpotManagement/>}/>
                            <Route path="yard/history" element={<TrailerHistoryPage/>}/>
                            <Route path="yard/spotter" element={<YardSpotterManagement/>}/>
                            <Route path="trailers" element={<Trailers/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={isAdmin(user)}/>}>
                            <Route path="users" element={<Users/>}/>
                            <Route path="users/:userId" element={<User/>}/>
                        </Route>
                        <Route element={<ProtectedRoute isAllowed={isDockCoordinator(user)}/>}>
                            <Route path="facilities" element={<Facilities/>}/>
                            <Route path="facilities/:facilityId" element={<Facility/>}/>
                            {/* Reports moved to Trailers page */}
                            {/* <Route path="reports" element={<Reports />} /> */}
                        </Route>
                        <Route
                            element={
                                <ProtectedRoute isAllowed={!!user && user.roles.includes('admin')}/>
                            }
                        ></Route>
                        <Route element={<RedirectRoute isAllowed={!!user}/>}>
                            <Route path="*" element={<h1>Not Found</h1>}/>
                        </Route>
                    </Routes>
                    <div><p>
                        Support: <a href="mailto:support@freightroll.com">support@freightroll.com</a>
                    </p></div>
                </div>
            </div>
        </UserContext.Provider>
    );
};

export default App;
